// FightPanel — renders vsj.fight. Each side is a served ORDERED LINE (front
// first): fight.player is the front ally's full control card, fight.foes[0]
// the full foe card, and foes[1..]/allies[1..] render as small queued cards
// behind in order — the front takes the melee hits. Structure built ONCE;
// rebinds update in place. fx `who` is the ATTACKER (`cid` = the foe
// involved), so juice lands on the victim's card — including a line-mate's
// mini when a ranged shot picks past the front: splat + flinch + BAR_FLASH
// ring, whole-panel shove when YOU take one; a kill flash + corpse drop;
// death overlay. Both full cards group their stats into labeled ATTACK /
// DEFENSE clusters of statvocab statBadges (served values; served
// dmg_math/hit_math workings ride the badge tooltips + a faint line).
// Quick-eat strip (ground badges; buttons come
// served-null mid-meal with served refusal prose), per-hand fists-reason
// lines, regen pie wound by the served {next_at, period_ms} pair (the tick
// itself is the served 'regen' fx — nothing inferred from patch diffs).

import { clear, el, flashClass, kbar, kbtn, tooltip } from '../kit';
import { serverNow } from '../net';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';
import { onFxType } from '../fx';
import { statBadge, statChip } from '../statvocab';

const LEAD_MS = 100;

function swingFrac(sw: any, now: number): number {
  if (!sw || typeof sw !== 'object' || sw.swing_ms == null || sw.ends_at == null) return 0;
  const ms = Math.max(1, Number(sw.swing_ms));
  const ends = Number(sw.ends_at) - LEAD_MS;
  return Math.max(0, Math.min(1, 1 - (ends - now) / ms));
}

export function fightPanel(): { root: HTMLElement; refresh(changed: Set<string>): void } {
  const emptyLabel = el('div', {
    class: 'k-dim',
    text: 'No fight. Start a Hunt from the Location tab when a creature den is near.',
  });

  // ---- player side ----
  // bars per stoneheim: hp = 8px capsule (green me / red foe), swing = 4px,
  // HP text above the bar, splats riding the bar wrap (.cmb-splats)
  const pHpLabel = el('span', { class: 'k-small k-dim' });
  const pHpBar = kbar('hp-me cmb', 8, true); // player heals ease up (animateGain)
  const pSplats = el('div', { class: 'cmb-splats' });
  const pBarWrap = el('div', { class: 'cmb-bar-wrap' }, pHpBar.root, pSplats);
  // per-hand ATTACK group: tiny caption + weapon name + swing bar + statBadges
  // (dmg/swt/hit, rng when ranged) + the served dmg_math faint working line +
  // fist-reason / range-note prose
  const hands: Record<string, { row: HTMLElement; name: HTMLElement; dura: HTMLElement; duraFill: HTMLElement; bar: ReturnType<typeof kbar>; stats: HTMLElement; math: HTMLElement; warn: HTMLElement }> = {};
  const handRows: HTMLElement[] = [];
  const handRanges: Record<string, HTMLElement> = {};
  for (const hand of ['main', 'off']) {
    const name = el('div');
    // served weapon durability {cur,max} — thin underbar under the name
    // (same .underbar pattern the activity card's tool chip uses)
    const duraFill = el('div');
    const dura = el('div', { class: 'underbar', style: 'position:static;display:none' }, duraFill);
    const bar = kbar('swing', 4);
    const stats = el('div', { class: 'badge-row' }); // statvocab badges
    // served full damage working (fight.player.<hand>.dmg_math) — verbatim
    const math = el('div', { class: 'k-small k-faint' });
    const warn = el('div', { class: 'k-small', style: 'color:var(--warn)' });
    // range note: served prose (fight.player.<hand>.range_note) — the driver
    // decides in/out of range and refreshes it on its tick
    const range = el('div', { class: 'k-small k-faint' });
    const row = el(
      'div',
      { class: 'cmb-group' },
      el('div', { class: 'cmb-caption', text: hand === 'main' ? 'Attack' : 'Attack — off hand' }),
      name, dura, bar.root, stats, math, warn, range,
    );
    hands[hand] = { row, name, dura, duraFill, bar, stats, math, warn };
    handRanges[hand] = range;
    handRows.push(row);
  }
  const pDefBadges = el('div', { class: 'badge-row' }); // statvocab badges
  const pDefense = el('div', { class: 'cmb-group' }, el('div', { class: 'cmb-caption', text: 'Defense' }), pDefBadges);
  const regenGauge = el('span', {
    style:
      'display:inline-block;width:16px;height:16px;border-radius:50%;flex:none;' +
      'border:1px solid var(--bar-chip-heal);transition:transform 0.2s',
  });
  const pRegen = el('span', { class: 'k-small k-faint' });
  const regenRow = el('div', { style: 'display:flex;gap:4px;align-items:center' }, regenGauge, pRegen);
  const eatingIcon = el('span', { style: 'width:18px;height:18px;flex:none;display:inline-flex' });
  const eatingLabel = el('span', { class: 'k-small k-dim' });
  const eatingBar = kbar('ok', 6); // stoneheim .eat-wrap .pbar height 6
  const eatBox = el(
    'div',
    { style: 'display:none;flex-direction:column;gap:2px' },
    el('div', { style: 'display:flex;gap:4px;align-items:center' }, eatingIcon, eatingLabel),
    eatingBar.root,
  );
  const eatStrip = el('div', { class: 'quick-eat' });
  const fleeHolder = el('div');
  const pPanel = el(
    'div',
    { class: 'k-panel raised combatant' },
    el(
      'div',
      { style: 'display:flex;gap:4px;align-items:baseline' },
      el('span', { class: 'k-title', style: 'flex:1', text: 'You' }),
      pHpLabel,
    ),
    pBarWrap,
    ...handRows,
    pDefense,
    regenRow,
    eatBox,
    eatStrip,
    fleeHolder,
  );

  // ---- foe side ----
  const fIconHolder = el('div', { style: 'width:58px;height:58px;flex:none' });
  const fName = el('div', {
    class: 'k-title',
    style: 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
  });
  const fTags = el('div', { style: 'display:flex;gap:4px;align-items:center' });
  const fHpLabel = el('div', { class: 'k-small k-dim' });
  const fHpBar = kbar('hp-enemy cmb', 8);
  const fSplats = el('div', { class: 'cmb-splats' });
  const fBarWrap = el('div', { class: 'cmb-bar-wrap' }, fHpBar.root, fSplats);
  const fSwingBar = kbar('swing', 4);
  // ATTACK group: vs-you badges (dmg_net/swt/hit_pct, math in the tooltips) +
  // the creature's damage-type tag + the served dmg_math working line
  const fAtkBadges = el('div', { class: 'badge-row' });
  const fMath = el('div', { class: 'k-small k-faint' });
  const fAttack = el(
    'div',
    { class: 'cmb-group' },
    el('div', { class: 'cmb-caption', text: 'Attack — vs you' }),
    fAtkBadges, fMath,
  );
  // DEFENSE group: arm/res/agi badges + acc/spd chips in a small mobility row
  const fDefBadges = el('div', { class: 'badge-row' });
  const fMobility = el('div', { class: 'stat-row' });
  const fDefense = el(
    'div',
    { class: 'cmb-group' },
    el('div', { class: 'cmb-caption', text: 'Defense' }),
    fDefBadges, fMobility,
  );
  // scouting/unknown foe keeps the "you'll see it" treatment
  const fScout = el('div', { class: 'k-small k-dim', text: "You'll see it when the fight starts." });
  const fPanel = el(
    'div',
    { class: 'k-panel raised combatant', style: 'cursor:pointer' },
    el(
      'div',
      { style: 'display:flex;gap:6px' },
      fIconHolder,
      el('div', { style: 'flex:1;min-width:0' }, fName, fTags),
    ),
    fHpLabel,
    fBarWrap,
    fSwingBar.root,
    fScout,
    fAttack,
    fDefense,
  );
  let fInfoId = '';
  fPanel.addEventListener('click', () => fInfoId && ui.select(fInfoId));

  // closing strip: served gap text (fight.closing.text) + a sanctioned
  // progress bar filling over started_at..ends_at
  const closingLabel = el('div', { class: 'k-small k-dim' });
  const closingBar = kbar('stamina', 8);
  const closingBox = el(
    'div',
    { style: 'display:none;flex-direction:column;gap:2px' },
    closingLabel,
    closingBar.root,
  );

  // ordered lines (front first): the front combatant gets the full card,
  // queued line-mates render as small cards behind it in served order
  const pQueue = el('div', { class: 'fight-queue' });
  const pSide = el('div', { class: 'fight-side' }, pPanel, pQueue);
  const fQueue = el('div', { class: 'fight-queue' });
  const fSide = el('div', { class: 'fight-side' }, fPanel, fQueue);
  const cols = el('div', { class: 'fight-cards', style: 'display:none' }, pSide, fSide);
  const scoutBar = kbar('stamina', 8);
  const scoutBox = el(
    'div',
    { style: 'display:none;flex-direction:column;gap:2px' },
    el('div', { class: 'k-small k-dim', text: 'Scouting the next foe...' }),
    scoutBar.root,
  );
  const root = el('div', { class: 'k-panel fight-panel' }, emptyLabel, closingBox, cols, scoutBox);

  // ---- state ----
  let fIconKey = '';
  let fIdSig = '';
  let fTagsSig = '';
  let fUnknown = false;
  let sils: string[] = []; // served silhouette cycle (fight.foes[0].sils)
  let silIdx = 0;
  let silTimer = 0;
  let eatSig = '';
  let fleeSig = '';
  let eatingItem = '';
  let eatingWaiting: boolean | null = null; // null forces a label refresh
  let regenDeadline = 0;
  let regenPeriodMs = 1;
  let fQueueSig = '';
  let pQueueSig = '';
  let frontCid: number | null = null; // foes[0].cid — fx cids land on cards
  const miniByCid: Record<number, HTMLElement> = {};

  const fight = (): any => {
    const f = vsj.at('fight');
    return f && typeof f === 'object' ? f : null;
  };

  // small line-mate card: icon + name + thin hp bar (served order)
  const renderQueue = (host: HTMLElement, list: any[], barCls: string, keepCids: boolean) => {
    clear(host);
    if (keepCids) for (const k of Object.keys(miniByCid)) delete miniByCid[Number(k)];
    for (const m of list) {
      if (!m || typeof m !== 'object') continue;
      const mini = el('div', { class: 'k-panel raised fight-mini' });
      const head = el('div', { style: 'display:flex;gap:4px;align-items:center;min-width:0' });
      if (!m.unknown && m.info) {
        const fi = vsj.info(String(m.info)) ?? {};
        head.append(icons.plated(String(fi.icon ?? m.info), 22, 3));
      }
      head.append(el('span', {
        class: 'k-small',
        style: 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
        text: m.unknown ? '???' : String(m.name ?? '?'),
      }));
      const bar = kbar(barCls, 6);
      bar.set(m.unknown || m.hp == null ? 0 : Number(m.hp) / Math.max(1, Number(m.hp_max ?? 1)));
      mini.append(head, bar.root);
      host.append(mini);
      if (keepCids && m.cid != null) miniByCid[Number(m.cid)] = mini;
    }
  };

  // hit splat — stoneheim curAct.js setSplatHandler verbatim: a .cmb-splat
  // span in the bar wrap's overlay at left 10+rand*70 %, gone at 900ms
  const splat = (host: HTMLElement, text: string, kind = '') => {
    if (!host.isConnected) return;
    const sp = el('span', { class: `cmb-splat${kind ? ` ${kind}` : ''}`, text });
    sp.style.left = `${10 + Math.random() * 70}%`;
    host.append(sp);
    setTimeout(() => sp.remove(), 900);
  };

  const drawGauge = (frac: number) => {
    regenGauge.style.background = `conic-gradient(var(--bar-chip-heal) ${frac * 360}deg, transparent 0)`;
  };

  const regenTick = (heal: number) => {
    regenGauge.style.transform = 'scale(1.22)';
    setTimeout(() => (regenGauge.style.transform = ''), 250);
    if (heal > 0) splat(pSplats, `+${heal}`, 'heal');
  };

  const rebindQuickEat = (qe: any, blocked: string | null) => {
    const sig = JSON.stringify([qe ?? null, blocked]);
    if (sig === eatSig) return;
    eatSig = sig;
    clear(eatStrip);
    if (!qe || typeof qe !== 'object') return;
    for (const item of Object.keys(qe).sort()) {
      const e = qe[item];
      if (!e || typeof e !== 'object') continue;
      // eat btn comes served-null while a meal runs; blocked = served prose
      const b = kbtn(e.eat, {
        label: `Eat (+${e.heal ?? '?'} hp) ×${e.count ?? 0}`, compact: true,
        denyReason: blocked ?? undefined,
      });
      b.prepend(icons.texture(item, 18));
      const from = String(e.from ?? 'bag');
      // secondary restores (fish stamina / root mana / berries both)
      const extra: string[] = [];
      if (e.stamina != null) extra.push(`${e.stamina} stamina`);
      if (e.mana != null) extra.push(`${e.mana} mana`);
      let tip = `Eat ${vsj.infoName(item)} (heals ${e.heal ?? '?'} hp${extra.length ? `, restores ${extra.join(' and ')}` : ''}).`;
      if (from === 'ground') tip += " It's lying on the ground here.";
      if (from === 'both') tip += ' In your bag and on the ground here.';
      tooltip(b, () => tip);
      eatStrip.append(b);
      if (from === 'ground' || from === 'both')
        eatStrip.append(
          el('span', {
            class: 'k-chip',
            style: `border-color:${from === 'ground' ? 'var(--warn)' : 'var(--ok)'}`,
            text: from,
          }),
        );
    }
  };

  const rebindEating = (eating: any) => {
    const on = !!(eating && typeof eating === 'object');
    eatBox.style.display = on ? 'flex' : 'none';
    if (on) {
      const item = String(eating.item ?? '');
      if (item !== eatingItem) {
        eatingItem = item;
        clear(eatingIcon);
        eatingIcon.append(icons.texture(item, 18));
        eatingWaiting = null; // tick picks wait prose vs "Eating <name>"
        eatingBar.set(0);
      }
    } else eatingItem = '';
  };

  const rebind = () => {
    const f = fight();
    cols.style.display = f ? 'flex' : 'none';
    emptyLabel.style.display = f ? 'none' : '';
    scoutBox.style.display = f?.scouting ? 'flex' : 'none';
    if (!f) {
      fIdSig = '';
      drawGauge(0);
      closingBox.style.display = 'none';
      fQueueSig = pQueueSig = '';
      clear(fQueue);
      clear(pQueue);
      frontCid = null;
      return;
    }
    const p = f.player ?? {};
    pHpLabel.textContent = `${p.hp ?? '?'} / ${p.hp_max ?? '?'}`;
    pHpBar.setHp(Number(p.hp ?? 0), Number(p.hp_max ?? 1));
    for (const hand of ['main', 'off']) {
      const r = hands[hand];
      const prof = p[hand];
      const has = prof && typeof prof === 'object';
      r.row.style.display = has ? 'flex' : 'none';
      if (!has) continue;
      r.name.textContent = String(prof.weapon_name ?? prof.weapon ?? '?');
      // full per-hand combat block, statvocab badges (values served; the
      // served dmg_math/hit_math workings ride the tooltips + faint line)
      const swingMs = Number((p.swings ?? {})[hand]?.swing_ms ?? 0);
      const dmgMath = prof.dmg_math != null ? String(prof.dmg_math) : '';
      const hitMath = prof.hit_math != null ? String(prof.hit_math) : '';
      clear(r.stats);
      r.stats.append(
        statBadge('dmg', prof.dmg ?? '?', dmgMath),
        statBadge('swt', `${Math.round(swingMs / 100) / 10}s`),
        statBadge('hit', `${prof.hit_pct ?? '?'}%`, hitMath),
      );
      if (Number(prof.range_yd ?? 0) > 0) r.stats.append(statBadge('rng', `${prof.range_yd} yd`));
      // served ammo {item, name, count} — compact chip beside the rng badge
      const ammo = prof.ammo;
      if (ammo && typeof ammo === 'object') {
        const chip = el('span', { class: 'k-chip' });
        if (ammo.item != null) chip.append(icons.texture(String(ammo.item), 14));
        chip.append(el('span', { text: `${ammo.count ?? '?'} × ${ammo.name ?? vsj.infoName(String(ammo.item ?? ''))}` }));
        r.stats.append(chip);
      }
      if (prof.acc != null) r.stats.append(statChip('acc', prof.acc));
      // served weapon durability {cur,max} — underbar under the name; low
      // (<25%) tints like the item pane's bar
      const dura = prof.durability;
      if (dura && typeof dura === 'object' && Number(dura.max) > 0) {
        const frac = Math.max(0, Math.min(1, Number(dura.cur) / Number(dura.max)));
        r.dura.style.display = '';
        r.dura.classList.toggle('low', frac < 0.25);
        r.duraFill.style.width = `${frac * 100}%`;
      } else r.dura.style.display = 'none';
      r.math.textContent = dmgMath;
      r.math.style.display = dmgMath ? '' : 'none';
      const reason = prof.fist_reason_text;
      r.warn.textContent = reason ? String(reason) : '';
      r.warn.style.display = reason ? '' : 'none';
      // out-of-range prose is SERVED (null = in range) — rendered verbatim
      handRanges[hand].textContent = prof.range_note ? String(prof.range_note) : '';
    }
    // defense block: served fight values + the served derived dodge (display
    // composition only — no client stat math)
    clear(pDefBadges);
    pDefBadges.append(statBadge('arm', p.armor ?? 0), statBadge('res', p.resist ?? 0));
    const dodge = vsj.at('derived_stats.combat.dodge.with_eq');
    if (dodge != null) pDefBadges.append(statBadge('agi', dodge));

    // regen gauge: served clock pair; the tick is the served 'regen' fx
    const regen = p.regen ?? {};
    regenDeadline = Number(regen.next_at ?? 0);
    regenPeriodMs = Math.max(1, Number(regen.period_ms ?? 1));

    // closing strip: display state served whole (text refreshed by the driver)
    const cl = f.closing;
    const clOn = !!(cl && typeof cl === 'object');
    closingBox.style.display = clOn ? 'flex' : 'none';
    if (clOn) closingLabel.textContent = String(cl.text ?? '');

    rebindQuickEat(p.quick_eat, p.quick_eat_blocked ?? null);
    rebindEating(f.eating);
    const fs = JSON.stringify([p.flee ?? null, p.flee_blocked ?? null]);
    if (fs !== fleeSig) {
      fleeSig = fs;
      clear(fleeHolder);
      if (p.flee != null) fleeHolder.append(kbtn(p.flee, { label: 'Flee' }));
      // flee refused: served-null btn + served refusal prose → a disabled
      // Flee whose deny reason IS that prose
      else if (p.flee_blocked != null)
        fleeHolder.append(kbtn(null, { label: 'Flee', denyReason: String(p.flee_blocked) }));
    }

    // foes: the ORDERED LINE, front first — foes[0] is the full card, the
    // rest render as queued minis behind it
    const foes: any[] = Array.isArray(f.foes) ? f.foes : [];
    const foe = foes[0] ?? {};
    frontCid = foe.cid != null ? Number(foe.cid) : null;
    const fqSig = JSON.stringify(foes.slice(1).map((m: any) => [m?.cid, m?.name, m?.hp, m?.hp_max, !!m?.unknown]));
    if (fqSig !== fQueueSig) {
      fQueueSig = fqSig;
      renderQueue(fQueue, foes.slice(1), 'hp-enemy cmb', true);
    }
    // the ally line is served too (front takes hits) — today just the player
    const allies: any[] = Array.isArray(f.allies) ? f.allies : [];
    const pqSig = JSON.stringify(allies.slice(1).map((m: any) => [m?.name, m?.hp, m?.hp_max]));
    if (pqSig !== pQueueSig) {
      pQueueSig = pqSig;
      renderQueue(pQueue, allies.slice(1), 'hp-me cmb', false);
    }
    // served target verdict: when your ranged hand strikes past the front,
    // ring the targeted line-mate's mini so the player sees who it hits
    const targetCid = f.target_cid ?? p.target_cid ?? null;
    for (const k of Object.keys(miniByCid))
      miniByCid[Number(k)].classList.toggle(
        'targeted',
        targetCid != null && Number(k) === Number(targetCid) && Number(targetCid) !== frontCid,
      );
    const foeInfo = vsj.info(String(foe.info ?? '')) ?? {};
    fUnknown = !!foe.unknown;
    sils = fUnknown && Array.isArray(foe.sils) ? foe.sils.map(String) : [];
    fInfoId = fUnknown ? '' : String(foe.info ?? '');
    fName.textContent = fUnknown ? '???' : String(foe.name ?? foeInfo.name ?? '?');
    const iconKey = fUnknown ? '_unknown_' : String(foeInfo.icon ?? foe.info ?? '');
    if (iconKey !== fIconKey) {
      fIconKey = iconKey;
      clear(fIconHolder);
      clearInterval(silTimer);
      if (fUnknown && sils.length) {
        const holder = icons.plated(sils[silIdx % sils.length], 48, 4);
        holder.querySelector('.k-icon')?.classList.add('silhouette');
        fIconHolder.append(holder);
        silTimer = window.setInterval(() => {
          if (!fUnknown || !fIconHolder.isConnected || !sils.length) return;
          silIdx = (silIdx + 1) % sils.length;
          clear(fIconHolder);
          const h = icons.plated(sils[silIdx], 48, 4);
          h.querySelector('.k-icon')?.classList.add('silhouette');
          fIconHolder.append(h);
        }, 700);
      } else if (!fUnknown) fIconHolder.append(icons.plated(iconKey, 48, 4));
    }
    const tagsSig = fUnknown ? '?' : `${foe.tier}/${foe.rarity}/${foe.size}/${foe.rarity_text ?? ''}/${foe.size_text ?? ''}`;
    if (tagsSig !== fTagsSig) {
      fTagsSig = tagsSig;
      clear(fTags);
      if (fUnknown) fTags.append(el('span', { class: 'k-chip k-dim', text: '? tier' }));
      else {
        fTags.append(icons.tierBadge(String(foe.tier ?? '')));
        // baseline variants come served-NULL: a chip renders iff a name ships;
        // served rarity_text/size_text prose rides the chip tooltips
        if (foe.rarity != null) {
          const rc = el('span', { class: 'k-chip', style: `color:var(--rarity-${foe.rarity}, var(--text))`, text: String(foe.rarity) });
          if (foe.rarity_text != null) { const t = String(foe.rarity_text); tooltip(rc, () => t); }
          fTags.append(rc);
        }
        if (foe.size != null) {
          const sc = el('span', { class: 'k-chip', style: `color:var(--size-${foe.size}, var(--text-dim))`, text: String(foe.size) });
          if (foe.size_text != null) { const t = String(foe.size_text); tooltip(sc, () => t); }
          fTags.append(sc);
        }
      }
    }
    fScout.style.display = fUnknown ? '' : 'none';
    fAttack.style.display = fUnknown ? 'none' : '';
    fDefense.style.display = fUnknown ? 'none' : '';
    if (fUnknown) {
      fHpLabel.textContent = '? / ?';
      fIdSig = '';
      fHpBar.set(0);
      fSwingBar.set(0);
      clear(fAtkBadges);
      clear(fDefBadges);
      clear(fMobility);
      fMath.textContent = '';
    } else {
      fHpLabel.textContent = `${foe.hp ?? '?'} / ${foe.hp_max ?? '?'}`;
      const idSig = `${foe.info}/${foe.name}`;
      if (idSig !== fIdSig) {
        fIdSig = idSig;
        fHpBar.set(Number(foe.hp ?? 0) / Math.max(1, Number(foe.hp_max ?? 1))); // fresh spawn snaps
      } else fHpBar.setHp(Number(foe.hp ?? 0), Number(foe.hp_max ?? 1));
      // ATTACK: vs-you numbers (served live) as badges — served workings ride
      // the tooltips, the damage type is the creature info's served string
      const dmgMath = foe.dmg_math != null ? String(foe.dmg_math) : '';
      const hitMath = foe.hit_math != null ? String(foe.hit_math) : '';
      clear(fAtkBadges);
      fAtkBadges.append(statBadge('dmg', foe.dmg_net ?? '?', dmgMath));
      if (foe.swing_ms != null) fAtkBadges.append(statBadge('swt', `${Math.round(Number(foe.swing_ms) / 100) / 10}s`));
      fAtkBadges.append(statBadge('hit', `${foe.hit_pct ?? '?'}%`, hitMath));
      if (foeInfo.dmg_type != null)
        fAtkBadges.append(el('span', { class: 'k-chip k-dim', text: String(foeInfo.dmg_type) }));
      fMath.textContent = dmgMath;
      fMath.style.display = dmgMath ? '' : 'none';
      // DEFENSE: the foe's served profile (agi/acc newly served on the info)
      clear(fDefBadges);
      if (foeInfo.armor != null) fDefBadges.append(statBadge('arm', foeInfo.armor));
      if (foeInfo.resist != null) fDefBadges.append(statBadge('res', foeInfo.resist));
      if (foeInfo.agi != null) fDefBadges.append(statBadge('agi', foeInfo.agi));
      clear(fMobility);
      if (foeInfo.acc != null) fMobility.append(statChip('acc', foeInfo.acc));
      if (foeInfo.speed_yd_s != null) fMobility.append(statChip('spd', `${foeInfo.speed_yd_s} yd/s`));
    }
    fPanel.classList.toggle('glow', ui.glows('creature'));
  };
  tooltip(regenRow, () => {
    const amt = vsj.at('derived_stats.combat.hp_regen.with_eq');
    const per = amt != null ? `+${amt} hp` : 'Health';
    return `${per} regenerates every ${Math.round(regenPeriodMs / 1000)}s — the pie fills as the next tick nears.`;
  });

  // ---- fx ----
  // stoneheim ui/fx.js hit(): the struck panel flashes+flinches (fx-hit,
  // 260ms), a block just rings (fx-block, 280ms), and taking one shoves the
  // whole board (fx-nudge on the layout, 260ms)
  onFxType('hit', (fx: any) => {
    if (!root.isConnected || root.offsetParent === null) return;
    const mine = String(fx.who ?? 'player') === 'player'; // who = attacker
    // fx cid names the FOE involved: a ranged strike past the front lands
    // its juice on that line-mate's mini card instead of the front card
    const cid = fx.cid != null ? Number(fx.cid) : null;
    const mini = mine && cid != null && cid !== frontCid ? miniByCid[cid] : undefined;
    const victim = mine ? (mini ?? fPanel) : pPanel;
    const host = mine ? (mini ? null : fSplats) : pSplats;
    const kind = String(fx.kind ?? 'hit');
    if (kind === 'miss') {
      if (host) splat(host, 'miss', 'miss');
    } else if (kind === 'block') {
      flashClass(victim, 'fx-block');
      if (host) splat(host, 'block', 'block');
    } else {
      flashClass(victim, 'fx-hit');
      if (!mine) {
        const layout = document.getElementById('app');
        if (layout) flashClass(layout, 'fx-nudge');
      }
      if (host) splat(host, `-${fx.amount ?? 0}`);
    }
  });
  // the meal lands: the +N heal splat only — no shake, no ring (stoneheim
  // fx.js hit kind 'heal'); the hp bar's heal chip does the rest
  onFxType('eat', (fx: any) => {
    if (!fight() || root.offsetParent === null) return;
    const heal = Number(fx.heal ?? 0);
    if (heal > 0) splat(pSplats, `+${heal}`, 'heal');
  });
  // the regen tick is SERVED ({fx:'regen', hp}) — never inferred client-side
  onFxType('regen', (fx: any) => {
    if (!fight() || root.offsetParent === null) return;
    regenTick(Number(fx.hp ?? 0));
  });
  // the corpse drops out of the panel — stoneheim fx.js kill(): fx-slain on
  // the foe's big icon, 620ms (no screen wash in stoneheim)
  onFxType('kill', (fx: any) => {
    if (root.offsetParent === null) return;
    // a line-mate picked off past the front (kill cid on a mini) drops there
    const cid = fx?.cid != null ? Number(fx.cid) : null;
    const mini = cid != null && cid !== frontCid ? miniByCid[cid] : undefined;
    const icon = mini ?? fIconHolder.firstElementChild;
    if (icon) {
      flashClass(icon, 'fx-slain');
      setTimeout(() => icon.classList.remove('fx-slain'), 620);
    }
  });
  onFxType('death', (fx: any) => {
    const scrim = el(
      'div',
      {
        style:
          'position:fixed;inset:0;z-index:80;background:var(--death-scrim);display:flex;' +
          'align-items:center;justify-content:center;font-size:var(--font-title)',
        // served death prose when present; the classic copy is the fallback
        text: String(fx?.text ?? 'You wake at home.'),
      },
    );
    scrim.addEventListener('click', () => scrim.remove());
    document.body.append(scrim);
    setTimeout(() => scrim.remove(), 3000);
  });

  // ---- per-frame ----
  const tick = () => {
    requestAnimationFrame(tick);
    if (root.offsetParent === null) return;
    const f = fight();
    if (!f) return;
    const now = serverNow();
    const p = f.player ?? {};
    // closing bar: sanctioned started_at/ends_at fill (text is served on rebind)
    const cl = f.closing;
    if (cl && typeof cl === 'object') {
      const start = Number(cl.started_at ?? now);
      const ends = Number(cl.ends_at ?? now) - LEAD_MS;
      const span = Math.max(1, ends - start);
      closingBar.set(Math.max(0, Math.min(1, (now - start) / span)));
    }
    const swings = p.swings ?? {};
    for (const hand of ['main', 'off']) {
      const r = hands[hand];
      if (r.row.style.display === 'none') continue;
      r.bar.set(swingFrac(swings[hand], now));
    }
    const foe = (Array.isArray(f.foes) ? f.foes[0] : null) ?? {};
    if (!fUnknown) fSwingBar.set(swingFrac({ ends_at: foe.swing_ends_at, swing_ms: foe.swing_ms }, now));
    const regenS = Math.max(0, (regenDeadline - now) / 1000);
    pRegen.textContent = regenS > 0 ? `regen in ${Math.ceil(regenS)}s` : 'regenerating';
    drawGauge(1 - Math.max(0, Math.min(1, (regenDeadline - now) / regenPeriodMs)));
    if (f.eating && eatBox.style.display !== 'none') {
      // the bar is the CHEW ONLY (eat never overlaps a swing); before
      // starts_at it sits empty under the served wait prose
      const dur = Math.max(1, Number(f.eating.duration_ms ?? 1));
      const ends = Number(f.eating.ends_at ?? now);
      const starts = Number(f.eating.starts_at ?? ends - dur);
      const waiting = now < starts && f.eating.wait_text != null;
      if (waiting !== eatingWaiting) {
        eatingWaiting = waiting;
        eatingLabel.textContent = waiting
          ? String(f.eating.wait_text)
          : `Eating ${vsj.infoName(eatingItem)}`;
      }
      eatingBar.set(now < starts ? 0 : Math.max(0, Math.min(1, (now - starts) / dur)));
    }
    if (f.scouting) {
      // sanctioned bar: served ends_at + duration_ms (null on ancient saves)
      const dur = Number(f.scouting.duration_ms ?? 0);
      const left = Math.max(0, Number(f.scouting.ends_at ?? now) - LEAD_MS - now);
      scoutBar.set(dur > 0 ? 1 - Math.max(0, Math.min(1, left / dur)) : 0);
    }
  };
  requestAnimationFrame(tick);

  rebind();
  return {
    root,
    refresh(changed) {
      if (changed.has('fight') || changed.has('infos') || changed.has('guidance') || changed.has('*')) rebind();
    },
  };
}
